import type { ActivityLog, ActivityLogTab } from './types';
import { isAnonymousMergeEvent } from './activity-log-types';

/**
 * What the feed records about one row, wherever the row is shown.
 *
 * Expanding a row is the row's own event, so it travels with it: the page
 * this replaces fires it from inside the row
 * (`ui/app/components/activity-log-row/index.gts:100-110`), which is why
 * every surface rendering the row reports it rather than only the feed.
 */

/** The `Activity Tab` property value shared by all three page events. */
export function activityTabName(tab: ActivityLogTab): 'Identified' | 'Anonymous' {
  return tab === 'anonymous' ? 'Anonymous' : 'Identified';
}

/**
 * Wire value for a profile-merge **source**. Note the space: this is
 * `ActivityLogSourceType.ProfileMerge` from Ember (`ui/app/utils/activity-log-source-types.ts`),
 * not the similarly-named `LOG_TYPE.ProfileMerge` log type (`'profile_merge'`, underscore).
 */
const PROFILE_MERGE_SOURCE = 'profile merge';

/**
 * The log records an activity that came from merging two people — i.e. one of its *sources* is a
 * profile merge, which is not the same population as logs whose own type is `LOG_TYPE.ProfileMerge`
 * (those are the merge itself). Ported from Ember's `LogModel.isPeopleMergeEvent`
 * (`ui/app/models/log.ts`), which is the definition behind the historical `Merge Activity Type`
 * series — so switching this to the log type would silently break continuity with it.
 */
function isPeopleMergeEvent(log: ActivityLog): boolean {
  return (log.sources ?? []).some((source) => source.type === PROFILE_MERGE_SOURCE);
}

/** Properties for `Log: Activity Log Specific Activity Viewed` (a row expansion). */
export function specificActivityViewedProps(tab: ActivityLogTab, log: ActivityLog) {
  return {
    'Activity Tab': activityTabName(tab),
    'Merged Anonymous Event': isAnonymousMergeEvent(log),
    'Merge Activity Type': isPeopleMergeEvent(log),
  };
}
